import { ReactElement, useEffect, useRef } from "react";
import style from "./style.module.scss";
import { Color } from "@/src/styles/typescript/types/color";
import { WordStyling } from "@/src/utils/types/landingpage";
import purify from 'dompurify';

type Variant =
  | "heading-1-bold"
  | "heading-2-semibold"
  | "heading-2-medium"
  | "heading-2-demibold"
  | "heading-2-bold"
  | "heading-3-medium"
  | "heading-3-demibold"
  | "heading-3-bold"
  | "heading-4-regular"
  | "heading-4-medium"
  | "heading-4-semibold"
  | "heading-4-bold"
  | "heading-5-regular"
  | "heading-5-medium"
  | "heading-5-semibold"
  | "heading-5-bold"
  | "heading-6-regular"
  | "heading-6-medium"
  | "heading-6-semibold"
  | "heading-6-bold"
  | "title-4-bold"
  | "title-4-semibold"
  | "title-4-medium"
  | "title-4-regular"
  | "title-2-bold"
  | "title-2-semibold"
  | "title-2-medium"
  | "title-2-regular"
  | "subtitle-1-bold"
  | "subtitle-1-semibold"
  | "subtitle-1-medium"
  | "subtitle-2-regular"
  | "subtitle-2-bold"
  | "subtitle-2-semibold"
  | "subtitle-2-medium"
  | "subtitle-3-medium-1"
  | "subtitle-3-medium-2"
  | "subtitle-3-demibold"
  | "subtitle-3-bold-1"
  | "subtitle-3-bold-2"
  | "subtitle-3-regular"
  | "subtitle-4-medium"
  | "subtitle-4-demibold"
  | "subtitle-4-bold"
  | "subtitle-5-regular"
  | "subtitle-5-medium"
  | "subtitle-5-semibold"
  | "subtitle-5-bold"
  | "subtitle-6-regular"
  | "subtitle-6-medium"
  | "subtitle-6-semibold"
  | "subtitle-6-bold"
  | "body-1-bold"
  | "body-1-semibold"
  | "body-1-medium"
  | "body-1-regular"
  | "body-1-regular-1"
  | "body-1-regular-2"
  | "button-1-bold"
  | "button-2-bold"
  | "button-2-medium"
  | "caption-1-semibold"
  | "caption-1-medium"
  | "caption-1-regular"
  | "caption-2-regular"
  // web
  | "heading-large-1"
  | "paragraph";

type Align = "left" | "right" | "center";
const Font = ({
  children,
  id = "",
  ...props
}: {
  children?: string;
  variant?: Variant;
  align?: Align;
  color?: Color;
  device?: string;
  paragraph?: boolean;
  id?: string;
  fontStyle?: "italic" | "default";
  listWordStyling?: WordStyling[];
}): ReactElement => {
  const typographyDivRef = useRef<HTMLDivElement>(null)
  const typographySpanRef = useRef<HTMLSpanElement>(null)

  const deviceFontVariant: string =
    props.device === "web"
      ? `typography-web--${props.variant}`
      : `typography-mobile--${props.variant}`;

  const fontStyles: string = `${style.typography} ${
    style["typography-color--" + props.color]
  } ${style[deviceFontVariant]} ${style["typography-align--" + props.align]}
`;

  useEffect(() => {
    if (typeof children !== 'string' ||
      !props.listWordStyling) {
      return
    }

    const htmlString = props.listWordStyling?.reduce((html, item) => {
      const regex = new RegExp(`\\b${item.word}\\b`, 'gi');
      return html.replace(regex, `<span style="font-style: ${item.style};">${item.word}</span>`);
    }, children);
    const sanitizedHtml = purify.sanitize(htmlString)
    if (typographyDivRef.current) {
      typographyDivRef.current.innerHTML = sanitizedHtml
    }
    if (typographySpanRef.current) {
      typographySpanRef.current.innerHTML = sanitizedHtml
    }
  }, [children, props.listWordStyling])

  return (
    <>
      {props.paragraph ? (
        <div
          id={id}
          ref={typographyDivRef}
          className={fontStyles}
          style={{ fontStyle: props.fontStyle }}
        >
          {`${children}`}
        </div>
      ) : (
        <span
          id={id}
          ref={typographySpanRef}
          className={fontStyles}
          style={{ fontStyle: props.fontStyle }}
        >
          {`${children}`}
        </span>
      )}
    </>
  );
};

Font.defaultProps = {
  device: "mobile",
  color: "mine_shaft",
  align: "left",
  variant: "body-1-bold",
  paragraph: false,
  id: "",
};
export default Font;
